import { createGateway } from '@neureus/ai-gateway';
import type { AIGateway, ChatCompletionRequest } from '@neureus/ai-gateway';
import type {
  Chunk,
  EmbeddingConfig,
  RAGEnv,
} from '../types';
import { EmbeddingError } from '../types';
import { generateId } from '../core/utils';

// Cloudflare Workers AI types
interface CloudflareAIEmbeddingRequest {
  text: string[];
}

interface CloudflareAIEmbeddingResponse {
  result: {
    shape: number[];
    data: number[][];
  };
  success: boolean;
  errors?: Array<{ message: string; code: number }>;
}

/**
 * Embedding service that uses the AI Gateway for generating embeddings
 */
export class EmbeddingService {
  private gateway: AIGateway;
  private env: RAGEnv;
  private config: EmbeddingConfig;
  private requestIdCounter = 0;

  constructor(env: RAGEnv, config: EmbeddingConfig) {
    this.env = env;
    this.config = config;
    this.gateway = createGateway(env, {
      routing: {
        primary: config.provider,
        fallbacks: this.getFallbackProviders(config.provider) as any,
        loadBalancing: 'least_latency',
        failoverThreshold: 2,
      },
      cache: {
        enabled: true,
        ttl: 86400, // Cache embeddings for 24 hours
        strategy: 'exact',
        keyPrefix: 'emb:',
      },
    });
  }

  /**
   * Generate embeddings for a single chunk
   */
  async generateEmbedding(chunk: Chunk): Promise<number[]> {
    try {
      const embedding = await this.generateEmbeddings([chunk]);
      return embedding[0];
    } catch (error) {
      throw new EmbeddingError(
        `Failed to generate embedding for chunk ${chunk.id}: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Generate embeddings for multiple chunks in batch
   */
  async generateEmbeddings(chunks: Chunk[]): Promise<number[][]> {
    if (chunks.length === 0) {
      return [];
    }

    try {
      // Process in batches to avoid rate limits and memory issues
      const batchSize = Math.min(this.config.batchSize, 100);
      const embeddings: number[][] = [];
      
      for (let i = 0; i < chunks.length; i += batchSize) {
        const batchChunks = chunks.slice(i, i + batchSize);
        const batchEmbeddings = await this.processBatch(batchChunks);
        embeddings.push(...batchEmbeddings);
      }
      
      return embeddings;
    } catch (error) {
      throw new EmbeddingError(
        `Failed to generate embeddings for batch: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Generate embeddings for text chunks with enhanced content
   */
  async generateEnhancedEmbeddings(chunks: Chunk[]): Promise<number[][]> {
    // Enhance chunks with additional context for better embeddings
    const enhancedChunks = chunks.map(chunk => ({
      ...chunk,
      content: this.enhanceChunkContent(chunk),
    }));
    
    return this.generateEmbeddings(enhancedChunks);
  }

  /**
   * Generate embedding for a query
   */
  async generateQueryEmbedding(query: string): Promise<number[]> {
    try {
      const embedding = await this.callEmbeddingModel([query]);
      return embedding[0];
    } catch (error) {
      throw new EmbeddingError(
        `Failed to generate embedding for query: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Process a batch of chunks
   */
  private async processBatch(chunks: Chunk[]): Promise<number[][]> {
    const texts = chunks.map(chunk => this.prepareTextForEmbedding(chunk.content));
    
    let retries = 0;
    while (retries <= this.config.maxRetries) {
      try {
        return await this.callEmbeddingModel(texts);
      } catch (error) {
        retries++;
        if (retries > this.config.maxRetries) {
          throw error;
        }
        
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, retries - 1), 30000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw new Error('Max retries exceeded');
  }

  /**
   * Call the embedding model via Cloudflare Workers AI or fallback providers
   */
  private async callEmbeddingModel(texts: string[]): Promise<number[][]> {
    try {
      // Try Cloudflare Workers AI first if available
      if (this.config.provider === 'cloudflare' && 'AI' in this.env) {
        return await this.callCloudflareEmbedding(texts);
      }
      
      // Fallback to other providers via AI Gateway
      return await this.callProviderEmbedding(texts);
    } catch (error) {
      console.warn('Primary embedding provider failed, trying fallbacks:', error);
      
      // Try fallback providers
      for (const fallbackProvider of this.getFallbackProviders(this.config.provider)) {
        try {
          const tempConfig = { ...this.config, provider: fallbackProvider as any };
          const tempService = new EmbeddingService(this.env, tempConfig);
          return await tempService.callEmbeddingModel(texts);
        } catch (fallbackError) {
          console.warn(`Fallback provider ${fallbackProvider} failed:`, fallbackError);
        }
      }
      
      throw new EmbeddingError(
        `All embedding providers failed. Last error: ${error}`,
        error as Error
      );
    }
  }

  /**
   * Call Cloudflare Workers AI for embeddings
   */
  private async callCloudflareEmbedding(texts: string[]): Promise<number[][]> {
    const ai = (this.env as any).AI;
    if (!ai) {
      throw new Error('Cloudflare AI binding not available');
    }

    const embeddings: number[][] = [];
    
    // Process in batches to respect rate limits
    const batchSize = Math.min(this.config.batchSize, 50); // Cloudflare AI has stricter limits
    
    for (let i = 0; i < texts.length; i += batchSize) {
      const batch = texts.slice(i, i + batchSize);
      
      try {
        // Use Cloudflare's text embedding model
        const response = await ai.run('@cf/baai/bge-base-en-v1.5', {
          text: batch
        });
        
        if (!response.success) {
          throw new Error(`Cloudflare AI embedding failed: ${JSON.stringify(response.errors)}`);
        }
        
        embeddings.push(...response.data);
      } catch (error) {
        throw new EmbeddingError(
          `Cloudflare AI embedding batch failed: ${error}`,
          error as Error
        );
      }
    }
    
    return embeddings;
  }

  /**
   * Call provider-specific embedding APIs through AI Gateway
   */
  private async callProviderEmbedding(texts: string[]): Promise<number[][]> {
    const embeddings: number[][] = [];
    
    for (const text of texts) {
      try {
        let embedding: number[];
        
        switch (this.config.provider) {
          case 'openai':
            embedding = await this.callOpenAIEmbedding(text);
            break;
          case 'cohere':
            embedding = await this.callCohereEmbedding(text);
            break;
          case 'google':
            embedding = await this.callGoogleEmbedding(text);
            break;
          default:
            // Fallback to simulated embedding for unsupported providers
            embedding = await this.simulateEmbedding(text);
        }
        
        embeddings.push(embedding);
      } catch (error) {
        throw new EmbeddingError(
          `Provider ${this.config.provider} embedding failed for text: ${error}`,
          error as Error
        );
      }
    }
    
    return embeddings;
  }

  /**
   * Call OpenAI embedding API
   */
  private async callOpenAIEmbedding(text: string): Promise<number[]> {
    const response = await fetch('https://api.openai.com/v1/embeddings', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.env.OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: this.config.model,
        input: text,
        encoding_format: 'float',
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.data[0].embedding;
  }

  /**
   * Call Cohere embedding API
   */
  private async callCohereEmbedding(text: string): Promise<number[]> {
    const response = await fetch('https://api.cohere.ai/v1/embed', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.env.COHERE_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: this.config.model,
        texts: [text],
        input_type: 'search_document',
      }),
    });

    if (!response.ok) {
      throw new Error(`Cohere API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.embeddings[0];
  }

  /**
   * Call Google embedding API
   */
  private async callGoogleEmbedding(text: string): Promise<number[]> {
    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/embedding-001:embedContent?key=${this.env.GOOGLE_API_KEY}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'models/embedding-001',
        content: {
          parts: [{ text }]
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Google API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.embedding.values;
  }

  /**
   * Simulate embedding generation (fallback for unsupported providers)
   */
  private async simulateEmbedding(text: string): Promise<number[]> {
    // This is a fallback that generates a deterministic embedding
    // Only used when no real provider is available
    
    const dimensions = this.config.dimensions;
    const embedding = new Array(dimensions).fill(0);
    
    // Generate a simple hash-based embedding for demonstration
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = ((hash << 5) - hash + text.charCodeAt(i)) & 0xffffffff;
    }
    
    // Create a pseudo-random but deterministic embedding
    for (let i = 0; i < dimensions; i++) {
      const seed = hash ^ (i * 2654435761);
      embedding[i] = Math.sin(seed) * 0.5;
    }
    
    // Normalize the embedding
    const norm = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
    return embedding.map(val => val / norm);
  }

  /**
   * Prepare text for embedding by cleaning and truncating
   */
  private prepareTextForEmbedding(text: string): string {
    // Clean the text
    let cleanText = text
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();
    
    // Truncate if too long (most embedding models have token limits)
    const maxTokens = 8192; // Conservative limit for most models
    const estimatedTokens = cleanText.length / 4; // Rough estimation
    
    if (estimatedTokens > maxTokens) {
      const maxChars = maxTokens * 4;
      cleanText = cleanText.substring(0, maxChars);
      
      // Try to end at a word boundary
      const lastSpaceIndex = cleanText.lastIndexOf(' ');
      if (lastSpaceIndex > maxChars * 0.9) {
        cleanText = cleanText.substring(0, lastSpaceIndex);
      }
    }
    
    return cleanText;
  }

  /**
   * Enhance chunk content with metadata for better embeddings
   */
  private enhanceChunkContent(chunk: Chunk): string {
    const parts: string[] = [chunk.content];
    
    // Add title if available
    if (chunk.metadata.title) {
      parts.unshift(`Title: ${chunk.metadata.title}`);
    }
    
    // Add section headers if available
    if (chunk.metadata.headers && chunk.metadata.headers.length > 0) {
      const headerText = chunk.metadata.headers.join(' > ');
      parts.unshift(`Section: ${headerText}`);
    }
    
    // Add document context if available
    const metadata = chunk.metadata;
    if (metadata.custom) {
      Object.entries(metadata.custom).forEach(([key, value]) => {
        if (typeof value === 'string' && value.length < 100) {
          parts.push(`${key}: ${value}`);
        }
      });
    }
    
    return parts.join('\n\n');
  }

  /**
   * Get fallback providers for embedding generation
   */
  private getFallbackProviders(primaryProvider: string): string[] {
    const allProviders = ['cloudflare', 'openai', 'cohere', 'google', 'mistral'];
    return allProviders.filter(provider => provider !== primaryProvider);
  }

  /**
   * Validate embedding dimensions
   */
  validateEmbedding(embedding: number[]): boolean {
    if (!Array.isArray(embedding)) {
      return false;
    }
    
    if (embedding.length !== this.config.dimensions) {
      return false;
    }
    
    // Check for NaN or infinite values
    return embedding.every(val => 
      typeof val === 'number' && 
      isFinite(val) && 
      !isNaN(val)
    );
  }

  /**
   * Calculate embedding similarity
   */
  calculateSimilarity(embedding1: number[], embedding2: number[]): number {
    if (embedding1.length !== embedding2.length) {
      throw new Error('Embeddings must have the same dimensions');
    }
    
    // Cosine similarity
    let dotProduct = 0;
    let norm1 = 0;
    let norm2 = 0;
    
    for (let i = 0; i < embedding1.length; i++) {
      dotProduct += embedding1[i] * embedding2[i];
      norm1 += embedding1[i] * embedding1[i];
      norm2 += embedding2[i] * embedding2[i];
    }
    
    if (norm1 === 0 || norm2 === 0) {
      return 0;
    }
    
    return dotProduct / (Math.sqrt(norm1) * Math.sqrt(norm2));
  }

  /**
   * Get embedding statistics for monitoring
   */
  getEmbeddingStats(): {
    provider: string;
    model: string;
    dimensions: number;
    batchSize: number;
  } {
    return {
      provider: this.config.provider,
      model: this.config.model,
      dimensions: this.config.dimensions,
      batchSize: this.config.batchSize,
    };
  }

  /**
   * Update embedding configuration
   */
  updateConfig(newConfig: Partial<EmbeddingConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Recreate gateway if provider changed
    if (newConfig.provider && newConfig.provider !== this.config.provider) {
      this.gateway = createGateway(this.env, {
        routing: {
          primary: this.config.provider,
          fallbacks: this.getFallbackProviders(this.config.provider) as any,
          loadBalancing: 'least_latency',
          failoverThreshold: 2,
        },
        cache: {
          enabled: true,
          ttl: 86400,
          strategy: 'exact',
          keyPrefix: 'emb:',
        },
      });
    }
  }

  /**
   * Create embedding service with default configuration
   */
  static createDefault(env: RAGEnv): EmbeddingService {
    // Prefer Cloudflare Workers AI if available, otherwise OpenAI
    const preferCloudflare = 'AI' in env;
    
    const defaultConfig: EmbeddingConfig = {
      model: env.RAG_DEFAULT_EMBEDDING_MODEL || (preferCloudflare ? '@cf/baai/bge-base-en-v1.5' : 'text-embedding-ada-002'),
      provider: preferCloudflare ? 'cloudflare' : 'openai',
      dimensions: preferCloudflare ? 768 : 1536,
      batchSize: parseInt(env.RAG_BATCH_SIZE || (preferCloudflare ? '50' : '100')),
      maxRetries: 3,
      timeout: 30000,
    };
    
    return new EmbeddingService(env, defaultConfig);
  }

  /**
   * Create embedding service for specific model
   */
  static createForModel(env: RAGEnv, model: string, provider: string): EmbeddingService {
    const modelConfigs: Record<string, Partial<EmbeddingConfig>> = {
      // OpenAI models
      'text-embedding-ada-002': { dimensions: 1536, batchSize: 100 },
      'text-embedding-3-small': { dimensions: 1536, batchSize: 100 },
      'text-embedding-3-large': { dimensions: 3072, batchSize: 50 },
      
      // Cohere models
      'embed-english-v3.0': { dimensions: 1024, batchSize: 96 },
      'embed-multilingual-v3.0': { dimensions: 1024, batchSize: 96 },
      
      // Cloudflare Workers AI models
      '@cf/baai/bge-base-en-v1.5': { dimensions: 768, batchSize: 50 },
      '@cf/baai/bge-large-en-v1.5': { dimensions: 1024, batchSize: 25 },
      '@cf/baai/bge-small-en-v1.5': { dimensions: 384, batchSize: 100 },
    };
    
    const modelConfig = modelConfigs[model] || {};
    
    const config: EmbeddingConfig = {
      model,
      provider: provider as any,
      dimensions: modelConfig.dimensions || 768,
      batchSize: modelConfig.batchSize || 50,
      maxRetries: 3,
      timeout: 30000,
      ...modelConfig,
    };
    
    return new EmbeddingService(env, config);
  }
}